<template>
  <div class="c-activity-timeline">
    <div class="c-activity-timeline__wrapper ph-no-capture dd-privacy-mask">
      <h1 class="c-activity-timeline__title">Activity Timeline</h1>
      <template v-if="!pageLoading">
        <BittsEmptyState
          v-if="showEmptyState"
          title="There's nothing to see here, yet"
          class="c-activity-timeline__empty-state lg:px-120"
          svg-name="timelineNoEventsYet"
        >
          <template #subtitle>
            <p class="text-center text-neutral-500 leading-6">
              {{ flaggedEmptyStateText }}

              <BittsLink
                v-if="!hasEvents"
                class="mt-4"
                text="View our help documentation"
                url="https://help.crossbeam.com/en/articles/3519493-create-reports"
              />
            </p>
            <RouterLink :to="{ name: 'reports.create' }">
              <BittsButton
                v-if="hasScope('write:reports') && !kafkaEvents.length"
                text="Create report"
                class="mt-24"
              />
            </RouterLink>
          </template>
        </BittsEmptyState>
        <ActivityTimeline
          v-else
          :events="filteredEvents"
          :account-name="accountName"
          class="mt-16"
        />
      </template>
      <div v-else class="pl-8 mt-20">
        <TimelineEventSkeleton v-for="skeleton in 4" :key="skeleton" />
      </div>
    </div>
    <TimelineSidebar
      v-if="!pageLoading"
      :selected-filters="selectedFilters"
      :partner-orgs-in-record="partnerOrgsInRecord"
      :org-filters="orgFilters"
      :partner-stack-is-active="partnerStackIsActive"
      :gong-is-active="gongIsActive"
      :visible="hasVisiblePartnerEvents"
      @org-filter-checked="orgClickHandler"
      @filter-checked="onCheckedKeys"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsEmptyState, BittsLink } from '@crossbeam/bitts';

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import ActivityTimeline from '@/components/ActivityTimeline.vue';
import TimelineEventSkeleton from '@/components/records/Timeline/TimelineEventSkeleton.vue';
import TimelineSidebar from '@/components/records/Timeline/TimelineSidebar.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIntegrations from '@/composables/useIntegrations';
import useReportTimelineEvents from '@/composables/useReportTimelineEvents';
import {
  ALL_EVENT_FILTERS,
  ATTRIBUTION_ACTIVITY,
  COPILOT_ACTIVITY,
  GONG,
  INTEGRATIONS,
  LIST_EVENT,
  LIST_NOTE_ADDED,
  LIST_NOTE_UPDATED,
  LIST_RECORD_ADDED,
  LIST_RECORD_REMOVED,
  OVERLAP_MOVEMENT,
  PARTNERSTACK,
  POPULATION_MOVEMENT,
  REPORT_EVENTS,
  REPORT_NOTE_ADDED,
  REPORT_OVERLAP_VISIBLE,
  REPORT_SHARED,
  SALES_EDGE_ACTIVITY,
  USER_ADDED_CONTACT_TO_CRM,
  USER_RESEARCHED_ACCOUNT,
  USER_RESEARCHED_CONTACT,
} from '@/constants/timeline';
import { captureException } from '@/errors';
import { usePartnersStore } from '@/stores';
import { Partner } from '@/types/partners';
import { TimelineEvent } from '@/types/timeline';

type EventType =
  | typeof GONG
  | typeof PARTNERSTACK
  | typeof ATTRIBUTION_ACTIVITY
  | typeof OVERLAP_MOVEMENT
  | typeof POPULATION_MOVEMENT
  | typeof SALES_EDGE_ACTIVITY
  | typeof COPILOT_ACTIVITY
  | typeof USER_ADDED_CONTACT_TO_CRM
  | typeof USER_RESEARCHED_ACCOUNT
  | typeof USER_RESEARCHED_CONTACT
  | typeof LIST_NOTE_ADDED
  | typeof LIST_NOTE_UPDATED
  | typeof LIST_RECORD_ADDED
  | typeof LIST_RECORD_REMOVED
  | typeof REPORT_NOTE_ADDED
  | typeof REPORT_OVERLAP_VISIBLE
  | typeof REPORT_SHARED
  | undefined;
type TimelineEventWithEventType = TimelineEvent & {
  eventType: EventType;
};

const {
  sourceId,
  sourcePrimaryKey,
  accountName = '',
} = defineProps<{
  sourceId: number;
  sourcePrimaryKey: string;
  accountName?: string;
}>();

const EVENT_TYPE_LOOKUP: Record<string, EventType> = {
  partner_mention: GONG,
  partnerstack_lead_created: PARTNERSTACK,
  attribution_created: ATTRIBUTION_ACTIVITY,
  attribution_updated: ATTRIBUTION_ACTIVITY,
  attribution_deleted: ATTRIBUTION_ACTIVITY,
  overlap_entry: OVERLAP_MOVEMENT,
  overlap_exit: OVERLAP_MOVEMENT,
  population_entry: POPULATION_MOVEMENT,
  population_exit: POPULATION_MOVEMENT,
  request_received: SALES_EDGE_ACTIVITY,
  request_sent: SALES_EDGE_ACTIVITY,
  user_added_contact_to_crm: USER_ADDED_CONTACT_TO_CRM,
  user_clicked_contacts_tab: USER_RESEARCHED_CONTACT,
  user_clicked_linkedin: USER_RESEARCHED_CONTACT,
  user_copied_email: USER_RESEARCHED_CONTACT,
  user_copied_phone: USER_RESEARCHED_CONTACT,
  user_clicked_account_tab: USER_RESEARCHED_ACCOUNT,
  user_clicked_play_details: USER_RESEARCHED_ACCOUNT,
  user_clicked_partners_tab: USER_RESEARCHED_ACCOUNT,
  list_note_added: LIST_NOTE_ADDED,
  list_note_updated: LIST_NOTE_UPDATED,
  list_record_added: LIST_RECORD_ADDED,
  list_record_removed: LIST_RECORD_REMOVED,
  report_note_added: REPORT_NOTE_ADDED,
  report_overlap_visible: REPORT_OVERLAP_VISIBLE,
  report_shared: REPORT_SHARED,
};

const EVENTS_WITHOUT_PARTNER = [
  'population_entry',
  'population_exit',
  'partnerstack_lead_created',
  'user_added_contact_to_crm',
  'user_clicked_contacts_tab',
  'user_clicked_linkedin',
  'user_copied_email',
  'user_copied_phone',
  'user_clicked_account_tab',
  'user_clicked_play_details',
];

const NUM_EVENTS_OFFSET = 20;

const filteredEvents = ref<TimelineEventWithEventType[]>([]);
const kafkaEvents = ref<TimelineEventWithEventType[]>([]);
const loadUntil = ref(NUM_EVENTS_OFFSET);
const pageLoading = ref(true);
const pageError = ref<Error | null>(null);
const polling = ref<ReturnType<typeof setInterval> | null>(null);
const orgFilters = ref<Record<string, boolean>>({});
const selectedFilters = ref<string[]>([]);
const isFetchingEvents = ref(false);

const { allEnabledIntegrations, partnerStackIsActive } = useIntegrations();
const { hasScope } = useAuth();

const { getPartnerOrgByUuid } = usePartnersStore();
const { isReportEventAllowed } = useReportTimelineEvents();

const eventFilterTypes = [
  { type: INTEGRATIONS, key: ALL_EVENT_FILTERS[INTEGRATIONS] },
  { type: ATTRIBUTION_ACTIVITY, key: ALL_EVENT_FILTERS[ATTRIBUTION_ACTIVITY] },
  { type: POPULATION_MOVEMENT, key: ALL_EVENT_FILTERS[POPULATION_MOVEMENT] },
  { type: OVERLAP_MOVEMENT, key: ALL_EVENT_FILTERS[OVERLAP_MOVEMENT] },
  { type: SALES_EDGE_ACTIVITY, key: ALL_EVENT_FILTERS[SALES_EDGE_ACTIVITY] },
];

const hasEvents = computed(() => kafkaEvents.value.length > 0);

const flaggedEmptyStateText = computed(() => {
  if (!hasEvents.value) {
    return `Activities between you and your partners on this account will appear here.
Try creating a report with your partner to see your overlaps.`;
  }
  if (hasEvents.value && !filteredEvents.value.length) {
    return `There are currently no activities that match these filters.
         Try expanding your search by removing filters.`;
  }
  return '';
});

const showEmptyState = computed(() => {
  return !pageLoading.value && !filteredEvents.value.length;
});

const hasSomeOrgFiltersSelected = computed(() => {
  return Object.values(orgFilters.value).some((val) => val);
});

const hasSomeFiltersSelected = computed(() => {
  return selectedFilters.value.length > 0;
});

const filterByIntegrations = filterByCategory(ALL_EVENT_FILTERS[INTEGRATIONS]);
const filterByCopilot = filterByCategory(ALL_EVENT_FILTERS[COPILOT_ACTIVITY]);
const filterByListEvents = filterByCategory(ALL_EVENT_FILTERS[LIST_EVENT]);
const filterByReportEvents = filterByCategory(ALL_EVENT_FILTERS[REPORT_EVENTS]);

const filterFunctions = {
  [PARTNERSTACK]: filterByIntegrations,
  [GONG]: filterByIntegrations,
  [ATTRIBUTION_ACTIVITY]: filterCrossbeamActivities,
  [OVERLAP_MOVEMENT]: filterCrossbeamActivities,
  [POPULATION_MOVEMENT]: filterCrossbeamActivities,
  [SALES_EDGE_ACTIVITY]: filterCrossbeamActivities,
  [COPILOT_ACTIVITY]: filterByCopilot,
  [USER_ADDED_CONTACT_TO_CRM]: filterByCopilot,
  [USER_RESEARCHED_ACCOUNT]: filterByCopilot,
  [USER_RESEARCHED_CONTACT]: filterByCopilot,
  [LIST_NOTE_ADDED]: filterByListEvents,
  [LIST_NOTE_UPDATED]: filterByListEvents,
  [LIST_RECORD_ADDED]: filterByListEvents,
  [LIST_RECORD_REMOVED]: filterByListEvents,
  [REPORT_NOTE_ADDED]: filterByReportEvents,
  [REPORT_OVERLAP_VISIBLE]: filterByReportEvents,
  [REPORT_SHARED]: filterByReportEvents,
};

const partnerOrgsInRecord = computed(() => {
  const partnerUuids = kafkaEvents.value.map(
    (event) => event.partner_organization_uuid,
  );
  return [...new Set(partnerUuids)].reduce((finalPartners, currentId) => {
    if (!currentId) return finalPartners;
    const partnerInStore = getPartnerOrgByUuid(currentId, false) as Partner;
    if (partnerInStore) finalPartners.push(partnerInStore);
    return finalPartners;
  }, [] as Partner[]);
});

const hasVisiblePartnerEvents = computed(() => {
  const partnerEvents = kafkaEvents.value.some(
    (event) => event?.partner_organization_uuid,
  );
  return !!partnerEvents;
});

const gongIsActive = computed(() => {
  return !!allEnabledIntegrations.value.find(
    (integration) => integration.type === 'tray_gong',
  );
});

watch(filteredEvents, () => {
  if (
    loadUntil.value > filteredEvents.value.length &&
    loadUntil.value > NUM_EVENTS_OFFSET &&
    hasSomeFiltersSelected.value &&
    hasSomeOrgFiltersSelected.value
  ) {
    loadUntil.value = filteredEvents.value.length;
  }
});

onMounted(async () => {
  await initialize();
  polling.value = setInterval(onPoll, 10000);

  selectedFilters.value = [
    ALL_EVENT_FILTERS[ATTRIBUTION_ACTIVITY],
    ALL_EVENT_FILTERS[POPULATION_MOVEMENT],
    ALL_EVENT_FILTERS[OVERLAP_MOVEMENT],
    ALL_EVENT_FILTERS[SALES_EDGE_ACTIVITY],
    ALL_EVENT_FILTERS[INTEGRATIONS],
    ALL_EVENT_FILTERS[COPILOT_ACTIVITY],
    ALL_EVENT_FILTERS[LIST_EVENT],
    ALL_EVENT_FILTERS[REPORT_EVENTS],
  ];

  partnerOrgsInRecord.value.forEach((partnerOrg) => {
    orgFilters.value[partnerOrg.uuid] = true;
  });

  filterEvents();
});

onUnmounted(() => {
  if (polling.value) {
    clearInterval(polling.value);
  }
});

async function onPoll() {
  if (isFetchingEvents.value) return;
  await updateTimeline();
}

function orgClickHandler({ id }: { id: string }) {
  orgFilters.value[id] = !orgFilters.value[id];
  filterEvents();
}

async function updateTimeline() {
  await fetchEvents();
}

async function initialize() {
  pageLoading.value = true;
  try {
    const partnersStore = usePartnersStore();
    const promises = [updateTimeline(), partnersStore.readySync];
    await Promise.all(promises);
  } catch (err) {
    pageError.value = err as Error;
    captureException(err);
  } finally {
    pageLoading.value = false;
  }
}

async function fetchEvents() {
  isFetchingEvents.value = true;

  const allEvents: TimelineEvent[] = [];
  const limit = 1000;
  let page = 1;
  let nextHref: string | null | undefined;

  const response = await crossbeamApi.GET(
    '/v0.1/activity-timeline/{source_id}/{master_id}',
    {
      params: {
        query: {
          limit,
          page,
        },
        path: {
          source_id: sourceId,
          master_id: sourcePrimaryKey,
        },
      },
    },
  );

  if (response) allEvents.push(...(response.data?.items ?? []));
  nextHref = response?.data?.pagination.next_href;
  page = (response?.data?.pagination.page ?? 0) + 1;

  while (nextHref) {
    const { data } = await crossbeamApi.GET(
      '/v0.1/activity-timeline/{source_id}/{master_id}',
      {
        params: {
          query: {
            page,
            limit,
          },
          path: {
            source_id: sourceId,
            master_id: sourcePrimaryKey,
          },
        },
      },
    );
    allEvents.push(...(data?.items ?? []));
    nextHref = data?.pagination.next_href;
    page = (data?.pagination.page ?? 0) + 1;
  }

  kafkaEvents.value = allEvents
    .filter(isReportEventAllowed)
    .map(addEventType)
    .filter(
      (event) =>
        event.eventType &&
        Object.values(EVENT_TYPE_LOOKUP).includes(event.eventType),
    );
  filterEvents();

  isFetchingEvents.value = false;
}

function addEventType(event: TimelineEvent): TimelineEventWithEventType {
  const eventType = event.type;
  return {
    ...event,
    eventType:
      eventType in EVENT_TYPE_LOOKUP
        ? EVENT_TYPE_LOOKUP[eventType as keyof typeof EVENT_TYPE_LOOKUP]
        : undefined,
  };
}

function onCheckedKeys({ keys }: { keys: string[] }) {
  selectedFilters.value = keys;
  filterEvents();
}

function filterEvents() {
  filteredEvents.value = kafkaEvents.value
    .filter((event) => {
      const defaultFilterFunction = () => false;
      const filterFunction =
        (event.eventType && filterFunctions[event.eventType]) ||
        defaultFilterFunction;
      return filterFunction(event);
    })
    .filter(filterByOrg);
}

function filterByCategory(allFilterKey: string) {
  return (event: TimelineEventWithEventType) => {
    if (selectedFilters.value?.includes(allFilterKey)) return true;
    return !!event.eventType && selectedFilters.value.includes(event.eventType);
  };
}

function filterCrossbeamActivities(event: TimelineEventWithEventType) {
  const eventParentType = event.eventType;
  const allSelector = eventFilterTypes.find(
    (eventFilterType) => eventFilterType.type === eventParentType,
  )?.key;

  if (allSelector && selectedFilters.value?.includes(allSelector)) {
    return true;
  }
  return selectedFilters.value?.includes(event.type);
}

function filterByOrg(event: TimelineEventWithEventType) {
  // don't filter out events that never have a partner on them
  if (EVENTS_WITHOUT_PARTNER.includes(event.type)) return event;
  if (!event.partner_organization_uuid) return event;
  return orgFilters.value[event.partner_organization_uuid];
}
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.c-activity-timeline {
  @apply grid grid-cols-10 gap-24;

  .c-activity-timeline__wrapper {
    @apply col-span-7;
  }

  .c-activity-timeline__title {
    @apply text-lg font-bold text-neutral-text-strong mb-8;
  }

  .c-activity-timeline__sidebar {
    @apply col-span-3 self-start sticky flex-1;
    --offset: 120px;
    top: 120px;
  }

  .c-activity-timeline__org-selector {
    @apply flex items-center justify-between py-12 px-16 border-b border-neutral-border;

    &:last-child {
      @apply border-0;
    }
  }
  .c-activity-timeline__empty-state {
    &.c-bitts-empty-state-large-border {
      @apply bg-white;
    }
    @apply px-24 py-72;
    img {
      @apply w-260;
    }
  }
}
</style>
